/* eslint-disable react-hooks/exhaustive-deps */
'use client'
import { userPrincipalAtom } from '@/atoms-store/user-atoms';
import { ADMIN_ROUTES, DOMAIN_ROUTES } from '@/components/navigation/route.config';
import { COOKIE_KEY } from '@/types/cookie';
import { ExtendedRoute } from '@/types/route';
import { UserPayload } from '@/types/userPayload';
import { useAtom } from 'jotai';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import useCookie from '../hooks/useCookie';
import { getStatus, redirectToLogin } from '../services/api.service';

interface AuthContextType {
    isAuthenticated: boolean;
    accessLevel: 'admin' | 'domain' | 'profile';
    loading: boolean;
    checkAuth: (forceUpdate: boolean) => {},
}

let authCheckPromise: Promise<void> | null = null;
const allRoutes = [...ADMIN_ROUTES, ...DOMAIN_ROUTES].map((route: Partial<ExtendedRoute>) => route?.path);

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
      throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default function AuthNGaurd({ children }: { children: ReactNode }) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessLevel, setAccessLevel] = useState<'admin' | 'domain' | 'profile'>('profile');
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(false);
    const [userContext, setUserContext] = useAtom<UserPayload>(userPrincipalAtom);
    const { getCookie, setCookie } = useCookie();
    const knownRoute: boolean = allRoutes.includes(pathname);

	// setCookie(COOKIE_KEY, 'eyJraWQiOiJBMDJpbTBVMTR1WmVvZ1VXIiwiYWxnIjoiUlMyNTYiLCJ0eXAiOiJKV1QifQ.eyJhdWQiOiJJWlE3QVVMV0dteHdmRzlIYjl5WjZDOFRCQzhNREJVVSIsInN1YiI6IjEwMDAyIiwiaXNzIjoiaHR0cHM6Ly9zYWFzLm5leHRzY20uY29tL2FjY291bnQyIiwiZXhwIjoxNzIyMjQ4Njk2LCJpYXQiOjE3MjIyNDUwOTYsImF1dGhfdGltZSI6MTcyMjI0NTA4OCwibmFtZSI6ImFkbWluIiwidXNlcm5hbWUiOiJhZG1pbiIsImF1dGhfbW9kZSI6IkJBU0lDIiwib3JnX25hbWUiOiJpbmNyZWZmIiwiYXBwX25hbWUiOiJhY2NvdW50Iiwib3JnX2lkIjoxMDAwMywic2lkIjoiOGE1MDNiY2Q2ZGFhNTg3NTM1ODQwZWFhNTVmZTVjNDk2MzliYTQxZiIsIm9yZ19yb2xlcyI6IltcImFkbWluXCIsXCJtYW5hZ2VyXCIsXCJzdXBlcmFkbWluXCJdIiwiYXBwX3JvbGVzIjoie1wiYXNzdXJlXCI6W1wiYWRtaW5cIixcImd1ZXN0XCJdLFwiYWNjb3VudFwiOltcInN0YW5kYXJkXCIsXCJpbnRlZ3JhdGlvblwiLFwiYWRtaW5cIixcImd1ZXN0XCJdfSIsInJlc291cmNlX3JvbGVzIjoie1wiYXNzdXJlXCI6e1wic3VwcGxpZXJcIjp7XCJzdXBwbGllcjNcIjpbXCJwYXJ0bmVyXCJdLFwic3VwcGxpZXIyXCI6W1wiYWRtaW5cIixcImRpc3RyaWJ1dGVyXCJdLFwic3VwcGxpZXIxXCI6W1wicGFydG5lclwiLFwiZGlzdHJpYnV0ZXJcIixcInZlbmRlclwiXX0sXCJjbGllbnRcIjp7XCJjbGllbnRcIjpbXCJhZG1pblwiXSxcImNsaWVudDJcIjpbXCJjbGllbnQuc3RhbmRhcmRcIixcImNsaWVudC5hZG1pblwiXSxcImNsaWVudDFcIjpbXCJjbGllbnQuc3RhbmRhcmRcIixcImNsaWVudC5hZG1pblwiXSxcImNsaWVudDBcIjpbXCJjbGllbnQuc3RhbmRhcmRcIixcImNsaWVudC5hZG1pblwiXX0sXCJ3YXJlaG91c2VcIjp7XCJ3YXJlaG91c2UxXCI6W1wid2gucGlja2VyLXBhY2tlclwiXX19fSJ9.QTsEwzm9WAHrCfERoNGJEIUtYuCp1MFuslg7Ry8AcZ_hcO-dTsCVvkZI6-n24Uf0Erqm3sSjTqs9AaNZJyZwPdxScO5YwmmsJ7oepXqV4Sa3N4wc2eRarxXXOqGRaIKkFr1lQfQ2ZiGm9NNh_7-GOzbh1A6585qlyFSTCo7_8xQ1po1-4Pjd4y7j14tA6cG4dzZUzz-KJt5B3RoEferN3meak8SM4rj7mfzqef_btqQCj9E4tv5AXRjxbQZvwnGmVPVhB0Zq5r900GdavlyPCnGDclJGnQ3MfxNM_dMkmDQ9vOubk_zMh-O08Abgihj7N5NOHGePOHeUHTyuDWuNbA', 1);

    const determineAccessLevel = (userDetail: UserPayload): 'admin' | 'domain' | 'profile' => {
        try {
            const orgRoles: string[] = userDetail.orgRoles;
            const appRoles: Record<string, string[]> = userDetail.appRoles || '{}';
            const resourceRoles: Record<string, Record<string, Record<string, string[]>>> = userDetail.resourceRoles || '{}';
      
          if (orgRoles.includes('superadmin')) return 'admin';
      
          if (orgRoles.includes('admin') || orgRoles.includes('manager')) return 'domain';
      
          if (Object.values(appRoles).some(roles => roles.includes('admin'))) return 'domain';
      
          if (Object.values(resourceRoles).some(category => 
                Object.values(category).some(resource => 
                    Object.values(resource).some(roles => roles.includes('admin'))
            ))) return 'domain';

          return 'profile';
        } catch (error) {
            console.error('Error parsing roles:', error);
            return 'profile';
        }
    };

    const checkAuth = async (forceUpdate: boolean = false) => {
        // If there's already an ongoing check, return that promise
        if (authCheckPromise) {
            return authCheckPromise;
        }

        // Create new auth check promise
        authCheckPromise = (async () => {
            setLoading(true);
            try {
                let payload: UserPayload | null = null;
                const storedPayload = localStorage.getItem('userPayload');

                if (storedPayload && !forceUpdate) {
                    payload = JSON.parse(storedPayload);
                } else {
                    payload = await getStatus();
                    localStorage.setItem('userPayload', JSON.stringify(payload));
                }
                
                const level = payload ? determineAccessLevel(payload) : 'profile';
                setUserContext(payload as UserPayload);
                setIsAuthenticated(true);
                setAccessLevel(level);
            } catch (error) {
                console.error('Error checking auth:', error);
                setIsAuthenticated(false);
                setAccessLevel('profile');
                setUserContext(null as any);
                localStorage.removeItem('userPayload');
                throw error; // Rethrow to handle in ProtectedRoute
            } finally {
                setLoading(false);
                // Clear the promise reference after completion
                authCheckPromise = null;
            }
        })();

        return authCheckPromise;
    };

    // const checkAuth = async (forceUpdate: boolean = false) => {
    //   console.log('check authorization is being called here');
    //   setLoading(true);

    //     try {
    //       // const decodedToken = jwtDecode(token) as UserPayload;
    //       // const level = determineAccessLevel(decodedToken);
    //       let payload: UserPayload | null = null;
    //       const storedPayload = localStorage.getItem('userPayload');

    //       if (storedPayload && !forceUpdate) {
    //           payload = JSON.parse(storedPayload);
    //       } else {
    //           payload = await getStatus();
    //           localStorage.setItem('userPayload', JSON.stringify(payload));
    //       }
    //       const level = payload ? determineAccessLevel(payload) : 'profile';
    //       setUserContext(payload as UserPayload);
    //       setIsAuthenticated(true);
    //       setAccessLevel(level);
    //     } catch (error) {
    //       console.error('Error decoding token:', error);
    //       setIsAuthenticated(false);
    //       setAccessLevel('profile');
    //       setUserContext(null as any);
    //       localStorage.removeItem('userPayload');
    //     }
    //     setLoading(false);
    // }

    async function checkSession() {
        // console.log('check session call here');
        // const _userObj = getDecodedUserToken();

        // // If user is not logged in(that means there is no ID token present in the cookie), redirect to login page
        // if(!_userObj) {
        //     console.log('user principal not found and redirecting to login page');
        //     setSession(false);
        //     window.location.href = loginAuthorize();
        //     // window.location.href = restService.redirectUrl(window.location.href);
        //     return;
        // }

        // if (_userObj) {
        //     setSession(true);
        //     setLastCheckTime(now.toString());
        //     console.log('user principal from localhost', _userObj);
        //     setUserContext(_userObj || {});
        //     return;
        // }


        // await getSession().then((res) => {
        //     setSession(true);
        //     setLastCheckTime(now.toString());
        //     setUserObj(res);
        //     setUserContext(res?.principal || {});
        // }).catch((err) => {
        //     setSession(false);
        //     window.location.href = restService.redirectUrl(window.location.href);
        //     return err;
        // })
    }

    // useEffect(() => {
    //     console.log('effect');
    //     // setSession(true);
    //     if (knownRoute) checkAuth(true);
    //     else console.log('Unknown route');
    // }, [pathname, searchParams])

    // useEffect(() => {
    //     if (!loading && !isAuthenticated) redirectToLogin();
    // }, [loading, isAuthenticated]);

    // const childNode = (
    //     <AuthContext.Provider value={{ isAuthenticated, accessLevel, loading }}>
    //         {children}
    //     </AuthContext.Provider>
    // )

    // if (!knownRoute) return childNode;
    // return session ? childNode : null; // Instead of null, we can redirect to 404 not found page instead of session call simpy return the chld node

    return (
        <AuthContext.Provider value={{ isAuthenticated, accessLevel, loading, checkAuth }}>
          {children}
        </AuthContext.Provider>
    );
}
